import React, { useEffect, useState } from "react";
import CircleSharpIcon from "@mui/icons-material/CircleSharp";
import { Box, Skeleton, Theme, Tooltip, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";

import SingleDataWithLabels from "./SingleDataWithLabels";
import { SkeletonWrapper, Summary } from "../widgets";

const PercentageLineStyles = (theme: Theme) => ({
  lineVisualFull: {
    display: "flex",
    height: "10px",
    margin: "15px 0",
  },
  lineVisualPart: {
    borderRadius: "3px",
    transition: "width 2s",
    marginRight: "2px",
  },
  lineLabels: {
    paddingTop: "10px",
    fontSize: "16px",
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
});
const PercentageLine = ({
  visualParts,
  leftLabel,
  loading,
  additionalStyles,
  showLabels = true,
  showSummaryValue = true,
  customValue,
  containerStyle,
  formatter = new Intl.NumberFormat("en-US").format,
  lineLabelValueTooltip,
  skipAppendingValueToLabel = false,
  percentageBase,
  percentageFractionDigits,
}: {
  visualParts: Summary[];
  leftLabel?: string;
  loading?: boolean;
  additionalStyles?: object;
  showLabels?: boolean;
  showSummaryValue?: boolean;
  customValue?: string;
  containerStyle?: object;
  formatter?: (value: number) => string;
  lineLabelValueTooltip?: string;
  skipAppendingValueToLabel?: boolean;
  /** Denominator for label percentages when parts can overlap or not cover the whole; defaults to the sum of parts. */
  percentageBase?: number;
  /** Decimal places for label percentages; non-zero values below the smallest step show as "<0.001%" (for 3). */
  percentageFractionDigits?: number;
}) => {
  const theme = useTheme();
  const styles = PercentageLineStyles(theme);
  const greyColor = theme.palette.grey["300"];

  const [lineLabelValueTooltipTitle, setLineLabelValueTooltipTitle] = useState<
    string | undefined
  >(undefined);
  const [data, setData] = useState<number[]>([]);
  const [widths, setWidths] = useState(
    visualParts.map(() => {
      return "0";
    })
  );

  const handlePercentage = (value: number, totalValue: number) => {
    return Math.round((value / totalValue) * 100);
  };

  useEffect(() => {
    setData(
      visualParts.map((item) => {
        return item.data as number;
      })
    );
  }, [visualParts]);

  useEffect(() => {
    if (lineLabelValueTooltip) {
      setLineLabelValueTooltipTitle(lineLabelValueTooltip);
    }
  }, [lineLabelValueTooltip]);

  const totalValue = data.reduce((a, b) => {
    return a + b;
  }, 0);

  const labelBase =
    percentageBase !== undefined && percentageBase > 0
      ? percentageBase
      : undefined;
  // Overlapping parts can exceed the base, so the bar scales to whichever is larger.
  const widthBase =
    labelBase === undefined ? totalValue : Math.max(totalValue, labelBase);

  const formatPreciseLabel = (value: number, digits: number) => {
    const percent = (value / (labelBase ?? totalValue)) * 100;
    const minimumPercent = 10 ** -digits;
    const isBelowMinimum = value !== 0 && Math.abs(percent) < minimumPercent;
    return isBelowMinimum
      ? `<${minimumPercent.toFixed(digits)}%`
      : `${percent.toLocaleString("en-US", { maximumFractionDigits: digits })}%`;
  };

  const labelPercentage = (value: number, index: number) => {
    if (percentageFractionDigits !== undefined) {
      return formatPreciseLabel(value, percentageFractionDigits);
    }
    return labelBase === undefined
      ? widths[index]
      : `${handlePercentage(value, labelBase)}%`;
  };

  useEffect(() => {
    const animation = () => {
      setWidths(data.map((value) => `${handlePercentage(value, widthBase)}%`));
    };
    const timerId = requestAnimationFrame(animation);
    return () => {
      cancelAnimationFrame(timerId);
    };
  }, [data, widthBase, setWidths]);

  const formattedNumber = (data: number | string) =>
    typeof data === "number" ? formatter(data) : data;

  const getCharts = (visualParts: Summary[]) => {
    if (loading) {
      return <Skeleton variant={"rounded"} width={"100%"} height={"10px"} />;
    } else if (totalValue === 0) {
      return (
        <Box
          sx={[
            {
              width: "100%",
              backgroundColor: greyColor,
            },
            styles.lineVisualPart,
          ]}
        />
      );
    }
    return visualParts.map((item, index) => {
      if (item.data !== 0) {
        const key = item.label + index;
        return (
          <Tooltip
            role={"listitem"}
            key={key}
            title={
              skipAppendingValueToLabel
                ? `${item.label}`
                : `${item.label}: ${formattedNumber(item.data)}`
            }
            followCursor
            placement="top"
            arrow
          >
            <Box
              key={item.label + item.data}
              sx={[
                {
                  width: widths[index] === "0%" ? "0.8%" : widths[index],
                  backgroundColor: item.color,
                },
                styles.lineVisualPart,
              ]}
            />
          </Tooltip>
        );
      }
    });
  };

  const getLineLabels = (visualParts: Summary[]) => {
    return visualParts.map((item, index) => {
      return (
        <Grid container sx={{ width: "100%" }} key={item.label + item.data}>
          <Grid size={7} sx={styles.lineLabels}>
            <SkeletonWrapper loading={loading}>
              <CircleSharpIcon sx={{ color: item.color, fontSize: "10px" }} />{" "}
              <span>{item.customLabel ?? item.label}</span>
            </SkeletonWrapper>
          </Grid>
          <Grid
            container
            size={5}
            justifyContent="flex-end"
            sx={styles.lineLabels}
          >
            <SkeletonWrapper loading={loading}>
              <Tooltip
                key={item.label + index}
                title={lineLabelValueTooltipTitle}
                arrow
                placement={"left"}
              >
                <span>
                  {formattedNumber(item.data)}
                  {` (${labelPercentage(item.data as number, index)})`}
                </span>
              </Tooltip>
            </SkeletonWrapper>
          </Grid>
        </Grid>
      );
    });
  };

  return (
    <div style={containerStyle}>
      {totalValue === 0 && !loading ? (
        <>
          <div>
            <SingleDataWithLabels
              label={leftLabel ?? visualParts[0].name}
              data={customValue ?? totalValue}
              loading={loading}
            />
            <div style={styles.lineVisualFull} role={"list"}>
              {getCharts(visualParts)}
            </div>
          </div>
        </>
      ) : (
        <>
          <div>
            <SingleDataWithLabels
              data={customValue ?? formatter(totalValue)}
              label={leftLabel ?? visualParts[0].name}
              loading={loading}
              showValue={showSummaryValue}
            />
            <div
              style={styles.lineVisualFull}
              role={"list"}
              aria-label={leftLabel}
            >
              {getCharts(visualParts)}
            </div>
          </div>
          <div
            style={{
              width: "100%",
              maxHeight: "170px",
              overflowY: "auto",
              ...additionalStyles,
            }}
          >
            {showLabels && totalValue ? getLineLabels(visualParts) : null}
          </div>
        </>
      )}
    </div>
  );
};

export default PercentageLine;
